import { assetFormFilter } from '@moko/utils';
import {
  Link,
  Panel,
  PanelType,
  SearchBox,
  DetailsList,
  SelectionMode,
} from '@fluentui/react';
import { useEffect, useState } from 'react';
import {
  getAssets,
  IPagedCollection,
  IAsset,
} from '../../services/assetServices';
import { useSetAtom } from 'jotai';
import {
  isInProgressAtom,
  errorMessageAtom,
} from '../../atoms/messageBarAtoms';

export interface IAssetSelectorProps {
  isOpen: boolean;
  allowedFormIds?: number[];
  tenantId?: number;
  hideAssetSelector: any;
  selectorPanelHeaderText?: string;
  onAssetLinkClick: (
    assetId: number,
    assetCode: string,
    assetName: string,
    assetUuid: string,
    formDefinitionId?: number
  ) => void;
}

const AssetSelectorPanel = (props: IAssetSelectorProps) => {
  const [assets, setAssets] = useState<IAsset[]>([]);
  const setIsInProgress = useSetAtom(isInProgressAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);

  useEffect(() => {
    if (!props.isOpen) {
      return;
    }

    const abortController = new AbortController();

    const fetchData = async () => {
      setIsInProgress(true);
      try {
        setAssets(
          (
            await getAssets(
              abortController,
              10,
              1,
              undefined,
              undefined,
              assetFormFilter(props.allowedFormIds ?? [], props.tenantId)
            )
          ).items
        );
      } catch (error: any) {
        console.error('Error:', error);
        setErrorMessage(error.message);
      } finally {
        setIsInProgress(false);
      }
    };

    fetchData();

    return () => {
      abortController.abort();
    };
  }, [
    props.isOpen,
    props.allowedFormIds,
    props.tenantId,
    setErrorMessage,
    setIsInProgress,
  ]);

  return (
    <Panel
      isLightDismiss
      isOpen={props.isOpen}
      onDismiss={props.hideAssetSelector}
      closeButtonAriaLabel="Close"
      type={PanelType.medium}
      headerText={props.selectorPanelHeaderText || 'Link existing asset'}
    >
      <SearchBox
        placeholder="Search asset"
        onSearch={(newValue) => {
          const abortController = new AbortController();
          getAssets(
            abortController,
            10,
            1,
            undefined,
            newValue,
            assetFormFilter(props.allowedFormIds ?? [], props.tenantId)
          ).then((assets: IPagedCollection<IAsset>) => {
            setAssets(assets.items);
          });
        }}
        onClear={() => setAssets([])}
      />
      <DetailsList
        columns={[
          {
            key: 'Id',
            name: 'Id',
            fieldName: 'id',
            minWidth: 50,
            maxWidth: 50,
            isResizable: true,
            onRender: (item) => (
              <Link
                key={item.assetId}
                onClick={() => {
                  props.onAssetLinkClick(
                    item.id,
                    item.code,
                    item.name,
                    item.uuid,
                    item.formDefinitionId
                  );
                  props.hideAssetSelector();
                }}
              >
                {item.id}
              </Link>
            ),
          },
          {
            key: 'Code',
            name: 'Code',
            fieldName: 'code',
            minWidth: 100,
            maxWidth: 125,
            isResizable: true,
          },
          {
            key: 'category',
            name: 'Category',
            fieldName: 'category',
            minWidth: 100,
            maxWidth: 150,
            isResizable: true,
          },
          {
            key: 'Name',
            name: 'Name',
            fieldName: 'name',
            minWidth: 250,
            maxWidth: 250,
            isResizable: true,
          },
        ]}
        compact={true}
        items={assets}
        selectionMode={SelectionMode.none}
      />
    </Panel>
  );
};

export default AssetSelectorPanel;
